import React, { useCallback, useMemo, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import Swal from "sweetalert2";

const customStyles = {
  content: {
    maxWidth: "600px",
    margin: "auto",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "none",
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 1008,
  },
  input: {
    width: "100%",
    padding: "8px",
    boxSizing: "border-box",
    margin: "10px 0",
  },
  fileInput: {
    width: "100%",
    margin: "10px 0",
    display: "block",
  },
  select: {
    width: "100%",
    padding: "8px",
    boxSizing: "border-box",
    margin: "10px 0",
  },
  button: {
    width: "30%",
    padding: "10px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontSize: "1rem",
    cursor: "pointer",
  },
};

export default function ModalFacturasClientes({
  isOpen,
  onClose,
  onSolicitudPendienteId,
}) {
  const [fileCer, setFileCer] = useState(null);
  const [fileKey, setFileKey] = useState(null);
  const [password, setPassword] = useState("");
  const [rfc, setRfc] = useState("");
  const [fechaInicial, setFechaInicial] = useState("");
  const [fechaFinal, setFechaFinal] = useState("");
  const [tipo, setTipo] = useState("Emitidas");
  const [loading, setLoading] = useState(false);

  const modalStyle = useMemo(
    () => ({
      content: customStyles.content,
      overlay: customStyles.overlay,
    }),
    [],
  );

  const resetForm = useCallback(() => {
    setFileCer(null);
    setFileKey(null);
    setPassword("");
    setRfc("");
    setFechaInicial("");
    setFechaFinal("");
    setTipo("Emitidas");
  }, []);

  const handleClose = useCallback(() => {
    // si quieres que al cerrar se mantenga lo escrito, quita resetForm()
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !fileCer ||
        !fileKey ||
        !password ||
        !rfc ||
        !fechaInicial ||
        !fechaFinal
      ) {
        alert("Por favor, llena todos los campos.");
        return;
      }

      const formData = new FormData();
      formData.append("fileCer", fileCer);
      formData.append("fileKey", fileKey);
      formData.append("password", password);
      formData.append("rfc", rfc);
      formData.append("fecha_inicial", fechaInicial);
      formData.append("fecha_final", fechaFinal);
      formData.append("tipo", tipo);

      try {
        setLoading(true);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/descargar-facturas`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (response?.data?.mensaje?.includes("duplicadas")) {
          Swal.fire({
            title: "Factura(s) duplicada(s)",
            icon: "warning",
            confirmButtonText: "Ok",
            scrollbarPadding: false,
          });
        } else {
          Swal.fire({
            title: "Solicitud enviada.",
            text: "Las facturas se actualizarán al completar la descarga.",
            icon: "success",
            confirmButtonText: "Ok",
            scrollbarPadding: false,
          });
        }

        handleClose();
      } catch (error) {
        console.error("Error al enviar la solicitud:", error);

        if (
          error?.response &&
          (error.response.status === 408 || error.response.status === 400)
        ) {
          const solicitudId = error.response.data?.solicitud_id || "";
          if (solicitudId && typeof onSolicitudPendienteId === "function") {
            onSolicitudPendienteId(solicitudId);
          }

          Swal.fire({
            title: "Solicitud enviada",
            text: `Tu solicitud fue enviada, regresa más tarde.${
              solicitudId ? " ID: " + solicitudId : ""
            }`,
            icon: "info",
            confirmButtonText: "Ok",
            scrollbarPadding: false,
          });

          handleClose();
          return;
        }

        alert("Ocurrió un error al procesar la solicitud.");
      } finally {
        setLoading(false);
      }
    },
    [
      fileCer,
      fileKey,
      password,
      rfc,
      fechaInicial,
      fechaFinal,
      tipo,
      handleClose,
      onSolicitudPendienteId,
    ],
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Descarga de Facturas"
      style={modalStyle}
    >
      <button
        onClick={handleClose}
        className="modal-close"
        style={{
          background: "transparent",
          border: "none",
          fontSize: "1.2rem",
          position: "absolute",
          top: "10px",
          right: "10px",
          cursor: "pointer",
        }}
        type="button"
      >
        <i className="bi bi-x-lg"></i>
      </button>

      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        Descarga de facturas
      </h2>

      <form onSubmit={handleSubmit} className="descarga-facturas-form">
        <div className="form-group">
          <label>Archivo CER</label>
          <input
            type="file"
            name="fileCer"
            style={customStyles.fileInput}
            onChange={(e) => setFileCer(e.target.files?.[0] || null)}
          />
        </div>

        <div className="form-group">
          <label>Archivo KEY</label>
          <input
            type="file"
            name="fileKey"
            style={customStyles.fileInput}
            onChange={(e) => setFileKey(e.target.files?.[0] || null)}
          />
        </div>

        <div className="form-group">
          <label>Contraseña de la FIEL</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={customStyles.input}
          />
        </div>

        <div className="form-group">
          <label>RFC</label>
          <input
            type="text"
            value={rfc}
            onChange={(e) => setRfc(e.target.value)}
            placeholder="Ingrese RFC"
            style={customStyles.input}
          />
        </div>

        <div className="form-group">
          <label>Fecha Inicial</label>
          <input
            type="date"
            value={fechaInicial}
            onChange={(e) => setFechaInicial(e.target.value)}
            style={customStyles.input}
          />
        </div>

        <div className="form-group">
          <label>Fecha Final</label>
          <input
            type="date"
            value={fechaFinal}
            onChange={(e) => setFechaFinal(e.target.value)}
            style={customStyles.input}
          />
        </div>

        <div className="form-group">
          <label>Tipo de Factura</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            style={customStyles.select}
          >
            <option value="Emitidas">Emitidas</option>
            {/* <option value="Recibidas">Recibidas</option> */}
          </select>
        </div>

        <button
          type="submit"
          className="generar-archivo-btn"
          style={customStyles.button}
          disabled={loading}
        >
          {loading ? "Procesando..." : "Solicitar Facturas"}
        </button>
      </form>

      {loading && (
        <p style={{ textAlign: "center", marginTop: "10px" }}>
          Procesando solicitud, por favor espere...
        </p>
      )}
    </Modal>
  );
}
